import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import IdleWarningDialog from '../components/IdleWarningDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INACTIVITY_TIMEOUT = 10 * 60 * 1000;
const ADMIN_ACTIVITY_KEY = 'dfl_admin_last_activity_at';
const WARNING_WINDOW_MS = 60 * 1000;

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const navigate = useNavigate();
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const warningOpenRef = useRef(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);

  // Get auth header for API calls - cookies handle auth automatically
  const getAuthHeader = useCallback(() => {
    return {};
  }, []);

  // Handle logout
  const logout = useCallback(async (showMessage = true, reason = '') => {
    try {
      await axios.post(`${API}/admin/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setAuthenticated(false);
    setCurrentUser(null);
    sessionStorage.removeItem(ADMIN_ACTIVITY_KEY);
    warningOpenRef.current = false;
    setWarningOpen(false);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    
    if (showMessage) {
      toast.success('Logged out successfully');
    }
    
    if (reason === 'inactivity') sessionStorage.setItem('dfl_session_expired', '1');
    window.location.assign(reason === 'inactivity' ? '/auth?reason=inactive' : '/auth');
  }, []);

  // Auto logout due to inactivity
  const autoLogout = useCallback(() => {
    logout(false, 'inactivity');
  }, [logout]);

  const showIdleWarning = useCallback(() => {
    const updateCountdown = () => {
      setSecondsRemaining(Math.max(0, Math.ceil((lastActivityRef.current + INACTIVITY_TIMEOUT - Date.now()) / 1000)));
    };
    updateCountdown();
    warningOpenRef.current = true;
    setWarningOpen(true);
    countdownTimerRef.current = window.setInterval(updateCountdown, 1000);
  }, []);

  const scheduleInactivityTimers = useCallback((activityAt) => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }
    warningOpenRef.current = false;
    setWarningOpen(false);

    if (authenticated) {
      const remaining = INACTIVITY_TIMEOUT - (Date.now() - activityAt);
      warningTimerRef.current = setTimeout(showIdleWarning, Math.max(0, remaining - WARNING_WINDOW_MS));
      inactivityTimerRef.current = setTimeout(() => {
        autoLogout();
      }, Math.max(0, remaining));
    }
  }, [authenticated, autoLogout, showIdleWarning]);

  // Reset inactivity timer after a deliberate activity event.
  const resetInactivityTimer = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    sessionStorage.setItem(ADMIN_ACTIVITY_KEY, String(now));
    setLastActivity(now);
    scheduleInactivityTimers(now);
  }, [scheduleInactivityTimers]);

  const recordAdminActivity = useCallback(() => {
    if (authenticated) resetInactivityTimer();
  }, [authenticated, resetInactivityTimer]);

  // Track user activity
  useEffect(() => {
    if (!authenticated) return;

    const activityEvents = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart'];
    
    const handleActivity = () => {
      if (warningOpenRef.current) return;
      if (Date.now() - lastActivityRef.current < 1000) return;
      resetInactivityTimer();
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Preserve an active session's existing idle deadline across a page refresh.
    scheduleInactivityTimers(lastActivityRef.current);

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, [authenticated, resetInactivityTimer, scheduleInactivityTimers]);

  // Verify authentication on mount - uses httpOnly cookie automatically
  const verifyAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/verify`);
      
      if (response.data.authenticated) {
        const lastSavedActivity = Number(sessionStorage.getItem(ADMIN_ACTIVITY_KEY));
        if (lastSavedActivity && Date.now() - lastSavedActivity >= INACTIVITY_TIMEOUT) {
          await axios.post(`${API}/admin/logout`);
          sessionStorage.setItem('dfl_session_expired', '1');
          window.location.assign('/auth?reason=inactive');
          return false;
        }
        setAuthenticated(true);
        setCurrentUser(response.data.user);
        lastActivityRef.current = lastSavedActivity || Date.now();
        if (!lastSavedActivity) {
          sessionStorage.setItem(ADMIN_ACTIVITY_KEY, String(lastActivityRef.current));
        }
        setLoading(false);
        return true;
      } else {
        setAuthenticated(false);
        setCurrentUser(null);
        sessionStorage.removeItem(ADMIN_ACTIVITY_KEY);
        setLoading(false);
        return false;
      }
    } catch (error) {
      setAuthenticated(false);
      setCurrentUser(null);
      sessionStorage.removeItem(ADMIN_ACTIVITY_KEY);
      setLoading(false);
      return false;
    }
  }, []);

  // Login function
  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { 
        username,
        password 
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        // Check if 2FA is required
        if (response.data.requires_2fa) {
          return { 
            success: false, 
            requires_2fa: true, 
            temp_token: response.data.temp_token 
          };
        }
        
        setAuthenticated(true);
        setCurrentUser(response.data.user);
        resetInactivityTimer();
        return { success: true, user: response.data.user };
      }
      return { success: false, error: 'Login failed' };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Invalid credentials' 
      };
    }
  };

  // Check if user has specific role
  const hasRole = useCallback((roles) => {
    if (!currentUser) return false;
    if (typeof roles === 'string') {
      return currentUser.role === roles;
    }
    return roles.includes(currentUser.role);
  }, [currentUser]);

  // Initial auth check
  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  const value = {
    authenticated,
    currentUser,
    loading,
    login,
    logout,
    verifyAuth,
    getAuthHeader,
    hasRole,
    lastActivity,
    recordAdminActivity
  };

  return (
    <>
      <AdminAuthContext.Provider value={value}>
        {children}
      </AdminAuthContext.Provider>
      <IdleWarningDialog open={warningOpen} secondsRemaining={secondsRemaining} onStaySignedIn={resetInactivityTimer} />
    </>
  );
};

export default AdminAuthContext;
